import { liquidGlassSurfaceFillSx } from '@focuslee/shared-mini-app';
import { MenuItem, Select, Stack } from "@mui/material";
import { Subheadline, Text } from '@telegram-apps/telegram-ui';
import React from "react";
import { usePlatform } from "../../hooks/usePlatform";

export type CustomSelectProps<T extends { id: string }> = {
  selectedId: string;
  setSelectedId: (id: string) => void;
  options: T[];
  before?: (item: T) => React.ReactNode;
  text: (item: T) => React.ReactNode;
  after?: (item: T) => React.ReactNode;
  renderSelected?: (item?: T) => React.ReactNode;
  renderSelectedBefore?: (item?: T) => React.ReactNode;
  renderSelectedAfter?: (item?: T) => React.ReactNode;
  disabled?: boolean;
}

export const CustomSelect = <T extends { id: string }>(props: CustomSelectProps<T>) => {
  const platform = usePlatform();
  const TypographyComponent = platform === 'ios' ? Text : Subheadline;
  const isSelected = !!props.selectedId;

  const handleRenderSelected = (selectedId: string) => {
    const selectedOption = props.options.find(o => o.id === selectedId);
    return (
      <Stack direction="row" alignItems="center" justifyContent="center">
        {props.renderSelectedBefore && props.renderSelectedBefore(selectedOption)}
        {props.renderSelected && <TypographyComponent style={{ marginLeft: '5px', color: 'var(--tg-theme-text-color)' }}>{props.renderSelected(selectedOption)}</TypographyComponent>}
        {props.renderSelectedAfter && props.renderSelectedAfter(selectedOption)}
      </Stack>
    );
  };
  return (
      <Stack direction="row" style={{ background: 'transparent' }}>
        <Select
          value={props.selectedId}
          onChange={(e) => props.setSelectedId(e.target.value)}
          disabled={props.disabled}
          sx={{
            boxShadow: 'none',
            filter: props.disabled ? 'grayscale(1)' : 'none',
            opacity: props.disabled ? 0.55 : 1,
            '.MuiSelect-select': {
              padding: '8px 14px !important',
              borderRadius: '999px !important',
              boxSizing: 'border-box',
              ...liquidGlassSurfaceFillSx,
              ...(isSelected && { border: '1.5px solid var(--tgui--button_color)' }),
            },
            '&.Mui-disabled .MuiSelect-select': {
              color: 'var(--tg-theme-text-color)',
              WebkitTextFillColor: 'var(--tg-theme-text-color)',
            },
            '.MuiOutlinedInput-notchedOutline': { border: 0 },
            "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
            "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 }
          }}
          MenuProps={{ slotProps: { paper: { style: { background: 'var(--tg-bg-color)', borderWidth: '0px', }} }}}
          IconComponent={() => <></>}
          renderValue={props.renderSelected ? handleRenderSelected : undefined}
          displayEmpty={!!props.renderSelected}
        >
          {props.options.map(item => (
            <MenuItem key={item.id} style={{ background: 'var(--tg-bg-color)', paddingTop: '0px' }} value={item.id}>
              <Stack direction="row" alignItems="center">
                {props.before && props.before(item)}
                <TypographyComponent style={{ marginLeft: '5px', color: 'var(--tg-theme-text-color)' }}>{props.text(item)}</TypographyComponent>
                {props.after && props.after(item)}
              </Stack>
            </MenuItem>
          ))}
        </Select>
      </Stack>
  );
}